<template>
  <!-- Uma página só não pede controle: setas apagadas dos dois lados prometem páginas que não existem. -->
  <nav v-if="paginas > 1" class="fish-paginacao" :aria-label="$t('common.paginacao.rotulo')">
    <div class="fish-paginacao__trilha">
      <FishButton
        tamanho="pequeno"
        icone="chevron-left"
        class="fish-paginacao__seta"
        :desabilitado="atual <= 1"
        :aria-label="$t('common.paginacao.anterior')"
        @click="ir(atual - 1)"
      />

      <ol class="fish-paginacao__numeros">
        <li v-for="(passo, i) in trilha" :key="`${passo}-${i}`">
          <span v-if="passo === null" class="fish-paginacao__salto" aria-hidden="true">…</span>
          <FishButton
            v-else
            tamanho="pequeno"
            class="fish-paginacao__numero t-numero"
            :variante="passo === atual ? 'primario' : 'neutro'"
            :aria-label="$t('common.paginacao.pagina', { n: passo })"
            :aria-current="passo === atual ? 'page' : null"
            @click="ir(passo)"
          >
            {{ passo }}
          </FishButton>
        </li>
      </ol>

      <!-- No telefone os números saem e fica o "2 / 7": nove placas de 44px não cabem em 360px. -->
      <span class="fish-paginacao__compacto t-numero" aria-hidden="true">{{ atual }} / {{ paginas }}</span>

      <FishButton
        tamanho="pequeno"
        icone="chevron-right"
        class="fish-paginacao__seta"
        :desabilitado="atual >= paginas"
        :aria-label="$t('common.paginacao.proxima')"
        @click="ir(atual + 1)"
      />
    </div>

    <p class="fish-paginacao__faixa t-numero" aria-live="polite">
      {{ $t('common.paginacao.faixa', { de: inicio, ate: fim, total }) }}
    </p>
  </nav>
</template>

<script>
import FishButton from './FishButton.vue';

/** Quantas casas a trilha mostra, contando o salto "…". Ímpar, para a atual ficar no meio. */
const CASAS = 7;

/**
 * `FishPaginacao` — o virar de página de uma lista longa (11/09/2026).
 *
 * Nasceu na Oficina, quando ela passou de 78 receitas; o `FishBook` já paginava, mas é **livro**,
 * com fitas e virada, e uma grade de cartas não é livro. Este é o controle sozinho: não sabe o que
 * está paginando, só quantos itens há e em qual página se está.
 *
 * ## A trilha tem tamanho fixo
 *
 * Primeira, última, a atual e uma vizinha de cada lado, com "…" onde pula. Sete casas **sempre**
 * — com a trilha crescendo e encolhendo, as setas mudavam de lugar a cada clique e o dedo que
 * estava em "›" caía num número.
 *
 * ## Quem rola a tela é quem usa
 *
 * Trocar de página com o controle no fim da lista deixa o jogador olhando o rodapé da página
 * nova. O primitivo não rola nada — ele não sabe onde a lista começa; a tela ouve
 * `update:modelValue` e leva o topo da lista para a vista.
 */
export default {
  name: 'FishPaginacao',
  components: { FishButton },
  props: {
    /** A página aberta, a partir de 1. */
    modelValue: { type: Number, required: true },
    /** Quantos itens a lista tem, no total. */
    total: { type: Number, required: true },
    porPagina: { type: Number, default: 12 },
  },
  emits: ['update:modelValue'],
  computed: {
    paginas() {
      return Math.max(1, Math.ceil(this.total / this.porPagina));
    },
    /** A página pedida encaixada no que existe — o pai pode pedir a 5 de uma lista que encolheu. */
    atual() {
      return Math.min(Math.max(1, this.modelValue), this.paginas);
    },
    inicio() {
      return this.total === 0 ? 0 : (this.atual - 1) * this.porPagina + 1;
    },
    fim() {
      return Math.min(this.atual * this.porPagina, this.total);
    },
    /** Os números da trilha; `null` é o salto. */
    trilha() {
      const n = this.paginas;
      const p = this.atual;
      if (n <= CASAS) return Array.from({ length: n }, (_, i) => i + 1);
      // Perto de uma ponta, o salto fica todo do outro lado: 1 2 3 4 5 … 12.
      if (p <= 4) return [1, 2, 3, 4, 5, null, n];
      if (p >= n - 3) return [1, null, n - 4, n - 3, n - 2, n - 1, n];
      return [1, null, p - 1, p, p + 1, null, n];
    },
  },
  methods: {
    ir(pagina) {
      const destino = Math.min(Math.max(1, pagina), this.paginas);
      if (destino !== this.atual) this.$emit('update:modelValue', destino);
    },
  },
};
</script>

<style scoped>
.fish-paginacao {
  display: grid;
  justify-items: center;
  gap: var(--e-1);
}

.fish-paginacao__trilha {
  display: flex;
  align-items: center;
  gap: var(--e-1);
}

.fish-paginacao__numeros {
  display: none;
  align-items: center;
  gap: var(--e-1);
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Largura fixa: "9" e "12" com placas de largura diferente faziam a trilha tremer ao virar. */
.fish-paginacao__numero {
  min-width: 48px;
}

.fish-paginacao__salto,
.fish-paginacao__compacto,
.fish-paginacao__faixa {
  color: var(--texto);
  font-size: var(--f-2);
}

.fish-paginacao__salto {
  min-width: 2ch;
  text-align: center;
}

.fish-paginacao__compacto {
  min-width: 7ch;
  text-align: center;
}

.fish-paginacao__faixa {
  margin: 0;
  font-size: var(--f-1);
}

@media (min-width: 600px) {
  .fish-paginacao__numeros {
    display: flex;
  }

  .fish-paginacao__compacto {
    display: none;
  }
}
</style>
